import { Button, buttonClasses } from "@/design-system/Button/Button";
import { Dialog } from "@/design-system/Dialog/Dialog";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { cn } from "@/lib/utils";
import { chromeExtensionUrl } from "@/settings";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as api from "../../../api/privateApi";
import { AppDispatch } from "../../../redux/store";
import { getUserSelector, updateUser } from "../../../redux/user/userSlice";

type Step = "rate" | "feedback" | "thanks";
const MIN_RATING_FOR_REVIEW = 4;

const CHROME_STORE_REVIEW_URL = chromeExtensionUrl("review_prompt", "/reviews");

/**
 * Raised by AppWrapper when the backend has queued an ASK_REVIEW notification:
 * the user just completed an SBC set through the Chrome extension. Five stars
 * sends them to the Web Store; anything lower asks what went wrong instead.
 */
export const ExtensionReviewDialog = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation(["sbcs", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);

  const [step, setStep] = useState<Step>("rate");
  const [rating, setRating] = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const ratedRef = useRef(false);

  useEffect(() => {
    mixpanelTrack("extension_review_prompt_shown");
    dispatch(updateUser({ extensionReviewStatus: "SHOWN" }));
    return () => {
      if (!ratedRef.current) {
        dispatch(updateUser({ extensionReviewStatus: "DISMISSED" }));
      }
    };
  }, [dispatch]);

  const handleSelectRating = (value: number) => {
    setRating(value);
    if (!ratedRef.current) {
      ratedRef.current = true;
      dispatch(updateUser({ extensionReviewStatus: "REVIEWED" }));
    }
    mixpanelTrack("extension_review_rated", { rating: value });
    setStep(value <= MIN_RATING_FOR_REVIEW ? "feedback" : "thanks");
  };

  const handleSendFeedback = async () => {
    if (!message.trim()) {
      setError(t("review.writeMessage"));
      return;
    }
    if (!user?.email) {
      setError(t("review.loginRequired"));
      return;
    }
    try {
      setSending(true);
      setError("");
      await api.sendMessage({
        email: user.email,
        message: `[Extension review - ${rating} star] ${message}`,
      });
      mixpanelTrack("extension_review_feedback_sent", { rating });
      setStep("thanks");
    } catch (e) {
      console.error(e);
      setError(t("common:error.generic"));
    } finally {
      setSending(false);
    }
  };

  const pitchStore = step === "thanks" && (rating ?? 0) > MIN_RATING_FOR_REVIEW;
  const active = hoverRating ?? rating ?? 0;

  const title =
    step === "rate"
      ? t("review.enjoying")
      : step === "feedback"
        ? t("review.improve")
        : pitchStore
          ? t("review.thanksFiveStars")
          : t("review.thanks");

  return (
    <Dialog isOpen onClose={onClose} title={title} negLabel="" posLabel="">
      {step === "rate" && (
        <div
          className="flex justify-center gap-1 pt-1"
          onMouseLeave={() => setHoverRating(null)}
        >
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              aria-label={t("review.rateStar", { count: value })}
              className="p-1.5 transition-transform hover:scale-110"
              onMouseEnter={() => setHoverRating(value)}
              onClick={() => handleSelectRating(value)}
            >
              <Star
                className={cn(
                  "size-9 transition-colors",
                  value <= active
                    ? "fill-amber-400 text-amber-400"
                    : "fill-transparent text-neutral-600",
                )}
              />
            </button>
          ))}
        </div>
      )}

      {step === "feedback" && (
        <div className="flex flex-col gap-3">
          <textarea
            autoFocus
            rows={4}
            maxLength={500}
            placeholder={t("review.messagePlaceholder")}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full resize-none rounded-[14px] border border-neutral-600 bg-neutral-800 px-3 py-2.5 text-base text-neutral-100 outline-none placeholder:text-neutral-500"
          />
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="medium"
              disabled={sending}
              onClick={handleSendFeedback}
            >
              {sending ? t("review.sending") : t("review.sendMessage")}
            </Button>
          </div>
        </div>
      )}

      {pitchStore && (
        <div className="flex flex-col gap-5">
          <p>
            <Trans
              ns="sbcs"
              i18nKey="review.chromeStorePitch"
              components={{ u: <span className="underline" /> }}
            />
          </p>
          {/* Rendered here rather than as the Dialog's positive action: that
              row is right-aligned and sized to its label, and this one wants
              to sit under the text at full width. */}
          <a
            href={CHROME_STORE_REVIEW_URL}
            target="_blank"
            rel="noopener"
            onClick={() => {
              mixpanelTrack("extension_review_chrome_store_clicked", {
                rating,
              });
              onClose();
            }}
            className={buttonClasses({ variant: "primary", stretch: true })}
          >
            {t("review.rateOnChromeStore")}
          </a>
        </div>
      )}
    </Dialog>
  );
};
